import { useEffect, useState, useCallback } from "react";
import { Navigate, Link } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { mediaUrl } from "@/lib/media";
import { useAuth } from "@/context/AuthContext";
import AppHeader from "@/components/AppHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2, Upload, ImageOff, Settings } from "lucide-react";

const KINDS = [
  { key: "products", label: "Products", name: "title",
    fields: [{ k: "title", l: "Title" }, { k: "description", l: "Description", type: "textarea" }, { k: "order", l: "Order", type: "number" }] },
  { key: "workshops", label: "Workshops", name: "title",
    fields: [{ k: "title", l: "Title" }, { k: "description", l: "Description", type: "textarea" }, { k: "price", l: "Price (₹)", type: "number" }, { k: "order", l: "Order", type: "number" }] },
  { key: "testimonials", label: "Testimonials", name: "name",
    fields: [{ k: "name", l: "Name" }, { k: "text", l: "Testimonial", type: "textarea" }, { k: "plan", l: "Plan / label" }, { k: "order", l: "Order", type: "number" }] },
  { key: "team", label: "Team", name: "name",
    fields: [{ k: "name", l: "Name" }, { k: "role", l: "Role" }, { k: "bio", l: "Bio", type: "textarea" }, { k: "order", l: "Order", type: "number" }] },
  { key: "meditations", label: "Meditations", name: "title", audio: true,
    fields: [{ k: "title", l: "Title" }, { k: "description", l: "Description", type: "textarea" }, { k: "tags", l: "Tags (comma separated)" }, { k: "order", l: "Order", type: "number" }] },
  { key: "remedies", label: "Remedies Store", name: "title",
    fields: [
      { k: "title", l: "Product title" },
      { k: "description", l: "Description", type: "textarea" },
      { k: "category", l: "Category (groups the store)" },
      { k: "format", l: "Format (e.g. 9 MP3 tracks)" },
      { k: "regular_price", l: "Regular Price (₹)", type: "number" },
      { k: "offer_price", l: "Offer Price (₹)", type: "number" },
      { k: "buy_link", l: "Razorpay link (optional, for later)" },
      { k: "drive_link", l: "Google Drive content link (delivered after payment)" },
      { k: "order", l: "Order", type: "number" },
    ] },
];

export default function Admin() {
  const { user } = useAuth();
  if (user && user.role !== "admin") return <Navigate to="/app" replace />;

  return (
    <div className="min-h-screen bg-background">
      <AppHeader />
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10">
        <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">Admin</p>
        <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
          <h1 className="font-serif text-4xl tracking-tight">Content manager</h1>
          <Link to="/admin/settings"><Button variant="outline" className="rounded-full border-border" data-testid="admin-settings-link"><Settings size={16} strokeWidth={1.5} /> Settings</Button></Link>
        </div>
        <Tabs defaultValue="products">
          <TabsList className="bg-secondary rounded-full p-1 flex-wrap h-auto">
            {KINDS.map((k) => <TabsTrigger key={k.key} value={k.key} className="rounded-full" data-testid={`admin-tab-${k.key}`}>{k.label}</TabsTrigger>)}
          </TabsList>
          {KINDS.map((k) => (
            <TabsContent key={k.key} value={k.key} className="pt-8"><KindManager kind={k} /></TabsContent>
          ))}
        </Tabs>
      </div>
    </div>
  );
}

function KindManager({ kind }) {
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null); // item or {} for new

  const load = useCallback(() => {
    api.get(`/admin/${kind.key}`).then((r) => setItems(r.data)).catch(() => setItems([]));
  }, [kind.key]);
  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    try {
      await api.delete(`/admin/${kind.key}/${id}`);
      toast.success("Deleted.");
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-5">
        <h2 className="font-serif text-2xl">{kind.label}</h2>
        <Button onClick={() => setEditing({})} className="rounded-full bg-primary text-primary-foreground hover:bg-primary/85 font-semibold" data-testid={`admin-add-${kind.key}`}><Plus size={16} strokeWidth={1.5} /> Add {kind.label.replace(/s$/, "")}</Button>
      </div>
      {items === null ? (
        <div className="py-16 grid place-items-center"><Loader2 className="animate-spin text-primary" strokeWidth={1.5} /></div>
      ) : (
        <div className="space-y-3" data-testid={`admin-list-${kind.key}`}>
          {items.length === 0 && <p className="text-muted-foreground text-sm py-8 text-center">No items yet.</p>}
          {items.map((it) => (
            <div key={it.id} className="flex items-center gap-4 rounded-xl border border-border bg-card p-4" data-testid={`admin-item-${it.id}`}>
              <div className="h-12 w-12 rounded-lg overflow-hidden bg-secondary grid place-items-center shrink-0">
                {it.image_url ? <img src={mediaUrl(it.image_url)} alt="" className="h-full w-full object-cover" /> : <ImageOff size={18} className="text-muted-foreground" strokeWidth={1.5} />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{it[kind.name]}</p>
                <p className="text-xs text-muted-foreground">Order {it.order ?? 0}{it.active === false ? " · hidden" : ""}</p>
              </div>
              <Button size="sm" variant="ghost" className="rounded-full" onClick={() => setEditing(it)} data-testid={`admin-edit-${it.id}`}><Pencil size={15} strokeWidth={1.5} /></Button>
              <Button size="sm" variant="ghost" className="rounded-full text-destructive hover:text-destructive" onClick={() => remove(it.id)} data-testid={`admin-delete-${it.id}`}><Trash2 size={15} strokeWidth={1.5} /></Button>
            </div>
          ))}
        </div>
      )}
      {editing && <Editor kind={kind} item={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function Editor({ kind, item, onClose, onSaved }) {
  const isNew = !item.id;
  const [form, setForm] = useState(() => {
    const f = { active: item.active !== false, image_url: item.image_url || "", audio_url: item.audio_url || "" };
    kind.fields.forEach((fl) => { f[fl.k] = item[fl.k] ?? (fl.type === "number" ? 0 : ""); });
    return f;
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingAudio, setUploadingAudio] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("image_url", data.url);
      toast.success("Image uploaded.");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const uploadAudio = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingAudio(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("audio_url", data.url);
      toast.success("Audio uploaded.");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setUploadingAudio(false); }
  };

  const save = async () => {
    setSaving(true);
    const payload = { ...form };
    kind.fields.forEach((fl) => { if (fl.type === "number") payload[fl.k] = Number(payload[fl.k]) || 0; });
    try {
      if (isNew) await api.post(`/admin/${kind.key}`, payload);
      else await api.put(`/admin/${kind.key}/${item.id}`, payload);
      toast.success("Saved.");
      onSaved();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-card border-border max-w-lg max-h-[85vh] overflow-y-auto" data-testid="admin-editor">
        <DialogHeader><DialogTitle className="font-serif text-2xl">{isNew ? `Add ${kind.label.replace(/s$/, "")}` : "Edit"}</DialogTitle>
          <DialogDescription>Fill in the details and save. Uploaded images are stored securely and shown on the public site.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {kind.fields.map((fl) => (
            <div key={fl.k} className="space-y-2">
              <Label htmlFor={fl.k}>{fl.l}</Label>
              {fl.type === "textarea" ? (
                <Textarea id={fl.k} rows={4} value={form[fl.k]} onChange={(e) => set(fl.k, e.target.value)} className="bg-secondary border-border" data-testid={`admin-field-${fl.k}`} />
              ) : (
                <Input id={fl.k} type={fl.type === "number" ? "number" : "text"} value={form[fl.k]} onChange={(e) => set(fl.k, e.target.value)} className="bg-secondary border-border" data-testid={`admin-field-${fl.k}`} />
              )}
            </div>
          ))}
          <div className="space-y-2">
            <Label>Image</Label>
            <div className="flex items-center gap-4">
              <div className="h-16 w-16 rounded-lg overflow-hidden bg-secondary grid place-items-center shrink-0">
                {form.image_url ? <img src={mediaUrl(form.image_url)} alt="" className="h-full w-full object-cover" /> : <ImageOff size={18} className="text-muted-foreground" strokeWidth={1.5} />}
              </div>
              <label className="flex items-center gap-2 text-sm text-primary hover:underline cursor-pointer" data-testid="admin-upload-label">
                {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} strokeWidth={1.5} />} Upload image
                <input type="file" accept="image/*" className="hidden" onChange={upload} data-testid="admin-image-input" />
              </label>
            </div>
          </div>
          {kind.audio && (
            <div className="space-y-2">
              <Label>Audio file</Label>
              {form.audio_url ? (
                <audio controls src={mediaUrl(form.audio_url)} className="w-full h-10" data-testid="admin-audio-preview" />
              ) : <p className="text-xs text-muted-foreground">No audio uploaded yet.</p>}
              <label className="flex items-center gap-2 text-sm text-primary hover:underline cursor-pointer w-fit" data-testid="admin-audio-upload-label">
                {uploadingAudio ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} strokeWidth={1.5} />} Upload audio (MP3/WAV/M4A)
                <input type="file" accept="audio/*" className="hidden" onChange={uploadAudio} data-testid="admin-audio-input" />
              </label>
            </div>
          )}
          <div className="flex items-center justify-between rounded-lg border border-border p-3">
            <Label htmlFor="active" className="cursor-pointer">Visible on site</Label>
            <Switch id="active" checked={form.active} onCheckedChange={(v) => set("active", v)} data-testid="admin-field-active" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-full border-border" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving || uploading || uploadingAudio} className="rounded-full bg-primary text-primary-foreground hover:bg-primary/85 font-semibold" data-testid="admin-save-btn">
            {saving && <Loader2 size={16} className="animate-spin" />} Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
